import { useState, useEffect } from "react";
import { Paper, Text, Button, Group, Loader, Alert, Table, Badge, Title, Grid, Card, ActionIcon, Menu, Switch } from "@mantine/core";
import { IconDots, IconEdit, IconTrash } from "@tabler/icons-react";
import { AddFamilyModal } from "./AddFamilyModal";
import { AddGuestModal } from "./AddGuestModal";
import { EditFamilyModal } from "./EditFamilyModal";
import { EditGuestModal } from "./EditGuestModal";
import { api, type FamilyWithGuests, type Statistics, type Guest } from "../services/api";

type Family = FamilyWithGuests;

export function AdminDashboard() {
  const [families, setFamilies] = useState<Family[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [emails, setEmails] = useState<Array<{id: number, family_name: string, contact_email: string}>>([]);
  const [showEmails, setShowEmails] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addFamilyModalOpen, setAddFamilyModalOpen] = useState(false);
  const [addGuestModalOpen, setAddGuestModalOpen] = useState(false);
  const [editFamilyModalOpen, setEditFamilyModalOpen] = useState(false);
  const [editGuestModalOpen, setEditGuestModalOpen] = useState(false);
  const [selectedFamilyId, setSelectedFamilyId] = useState<number | null>(null);
  const [selectedFamily, setSelectedFamily] = useState<FamilyWithGuests | null>(null);
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [familiesData, statsData] = await Promise.all([
        api.getAllFamiliesWithGuests(),
        api.getStatistics()
      ]);
      
      setFamilies(familiesData);
      setStatistics(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load admin data");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddGuest = (familyId: number) => {
    setSelectedFamilyId(familyId);
    setAddGuestModalOpen(true);
  };

  const handleEditFamily = (family: FamilyWithGuests) => {
    setSelectedFamily(family);
    setEditFamilyModalOpen(true);
  };

  const handleEditGuest = (guest: Guest) => {
    setSelectedGuest(guest);
    setEditGuestModalOpen(true);
  };

  const handleToggleBrunchInvite = async (guest: Guest) => {
    try {
      const newBrunchInvited = !guest.brunch_invited;
      await api.updateGuestBrunchInvitation(guest.id, newBrunchInvited);
      
      // Update local state instead of reloading all data
      setFamilies(prevFamilies => 
        prevFamilies.map(family => ({
          ...family,
          guests: family.guests.map(g => 
            g.id === guest.id 
              ? { ...g, brunch_invited: newBrunchInvited }
              : g
          )
        }))
      );
      
      // Update statistics
      setStatistics(prevStats => {
        if (!prevStats) return prevStats;
        
        return {
          ...prevStats,
          brunchInvitedGuests: newBrunchInvited 
            ? prevStats.brunchInvitedGuests + 1 
            : prevStats.brunchInvitedGuests - 1
        };
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update brunch invitation");
      console.error(err);
    }
  };
  
  const handleShowEmails = async () => {
    try {
      const emailsData = await api.getAllFamilyEmails();
      setEmails(emailsData);
      setShowEmails(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load emails");
      console.error(err);
    }
  };
  
  const handleCopyAllEmails = () => {
    const emailList = emails.map(e => e.contact_email).join(', ');
    navigator.clipboard.writeText(emailList);
  };

  const getStatusBadge = (confirmed: boolean | null) => {
    if (confirmed === true) {
      return <Badge color="green" size="sm">Attending</Badge>;
    } else if (confirmed === false) {
      return <Badge color="red" size="sm">Not Attending</Badge>;
    } else {
      return <Badge color="gray" size="sm">No Response</Badge>;
    }
  };

  if (isLoading) {
    return (
      <Paper p="xl" shadow="sm" style={{ textAlign: "center" }}>
        <Loader size="lg" />
        <Text style={{ marginTop: "1rem" }}>Loading admin dashboard...</Text>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper p="xl" shadow="sm">
        <Alert color="red" title="Error">
          {error}
        </Alert>
        <Button onClick={loadData} style={{ marginTop: "1rem" }}>
          Retry
        </Button>
      </Paper>
    );
  }

  return (
    <div>
      <Paper p="xl" shadow="sm" style={{ marginBottom: "2rem" }}>
        <Group justify="space-between" style={{ marginBottom: "1.5rem" }}>
          <Title order={2}>Admin Dashboard</Title>
          <Group>
            <Button onClick={handleShowEmails} color="green" variant="light">
              📧 View All Emails
            </Button>
            <Button onClick={() => setAddFamilyModalOpen(true)} color="blue">
              + Add Family
            </Button>
            <Button onClick={loadData} variant="light">
              Refresh
            </Button>
          </Group>
        </Group>
        
        {showEmails && (
          <Paper p="md" style={{ marginBottom: "2rem", backgroundColor: "#f1f3f5" }}>
            <Group justify="space-between" style={{ marginBottom: "1rem" }}>
              <Text fw={600}>Contact Emails ({emails.length})</Text>
              <Group>
                <Button size="sm" onClick={handleCopyAllEmails}>
                  Copy All
                </Button>
                <Button size="sm" variant="subtle" onClick={() => setShowEmails(false)}>
                  Close
                </Button>
              </Group>
            </Group>
            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              {emails.map((email) => (
                <div key={email.id} style={{ padding: "0.5rem", borderBottom: "1px solid #dee2e6" }}>
                  <Text size="sm" fw={500}>{email.family_name}</Text>
                  <Text size="sm" c="dimmed">{email.contact_email}</Text>
                </div>
              ))}
            </div>
          </Paper>
        )}

        {statistics && (
          <Grid gutter="md" style={{ marginBottom: "2rem" }}>
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <Card shadow="xs" padding="lg">
                <Text size="sm" c="dimmed">Total Families</Text>
                <Text size="xl" fw={700}>{statistics.totalFamilies}</Text>
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <Card shadow="xs" padding="lg">
                <Text size="sm" c="dimmed">Total Guests</Text>
                <Text size="xl" fw={700}>{statistics.totalGuests}</Text>
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <Card shadow="xs" padding="lg" style={{ border: "2px solid #40c057" }}>
                <Text size="sm" c="dimmed">Confirmed Attending</Text>
                <Text size="xl" fw={700} c="green">{statistics.confirmedGuests}</Text>
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <Card shadow="xs" padding="lg" style={{ border: "2px solid #fa5252" }}>
                <Text size="sm" c="dimmed">Not Attending</Text>
                <Text size="xl" fw={700} c="red">{statistics.declinedGuests}</Text>
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <Card shadow="xs" padding="lg">
                <Text size="sm" c="dimmed">No Response</Text>
                <Text size="xl" fw={700}>{statistics.noResponseGuests}</Text>
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <Card shadow="xs" padding="lg" style={{ border: "2px solid #4dabf7" }}>
                <Text size="sm" c="dimmed">Confirmed Children</Text>
                <Text size="xl" fw={700} c="blue">
                  {families.reduce((count, family) => 
                    count + family.guests.filter(g => g.is_child && g.confirmed === true).length, 0
                  )} 👶
                </Text>
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <Card shadow="xs" padding="lg" style={{ border: "2px solid #f59f00" }}>
                <Text size="sm" c="dimmed">Brunch Invited</Text>
                <Text size="xl" fw={700} c="orange">{statistics.brunchInvitedGuests} 🥐</Text>
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <Card shadow="xs" padding="lg" style={{ border: "2px solid #20c997" }}>
                <Text size="sm" c="dimmed">Brunch Confirmed</Text>
                <Text size="xl" fw={700} c="teal">{statistics.brunchConfirmedGuests} ✓</Text>
              </Card>
            </Grid.Col>
          </Grid>
        )}

        <div style={{ overflowX: "auto" }}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Family Name</Table.Th>
                <Table.Th>Unique Link</Table.Th>
                <Table.Th>Guests</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Dietary Restrictions</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {families.map((family) => {
                const attending = family.guests.filter(g => g.confirmed === true).length;
                const notAttending = family.guests.filter(g => g.confirmed === false).length;
                const noResponse = family.guests.filter(g => g.confirmed === null).length;

                return (
                  <Table.Tr key={family.id}>
                    <Table.Td>
                      <Group gap="xs">
                        <Text fw={500}>{family.family_name}</Text>
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="gray"
                          onClick={() => handleEditFamily(family)}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{family.unique_link}</Text>
                    </Table.Td>
                    <Table.Td>
                      <div>
                        {family.guests.map(guest => (
                          <Group key={guest.id} gap="xs" style={{ marginBottom: "0.25rem" }}>
                            <Text size="sm">{guest.first_name} {guest.last_name}</Text>
                            {guest.is_child && (
                              <Badge color="blue" size="xs" variant="light">👶</Badge>
                            )}
                            {guest.is_child && guest.age !== null && (
                              <Badge color="cyan" size="xs" variant="outline">{guest.age} ans</Badge>
                            )}
                            {getStatusBadge(guest.confirmed)}
                            <Switch
                              size="xs"
                              checked={guest.brunch_invited}
                              onChange={() => handleToggleBrunchInvite(guest)}
                              onLabel="🥐"
                              offLabel=""
                              title="Brunch invitation"
                            />
                            {guest.brunch_invited && guest.brunch_confirmed === true && (
                              <Badge color="teal" size="xs" variant="filled">Brunch ✓</Badge>
                            )}
                            {guest.brunch_invited && guest.brunch_confirmed === false && (
                              <Badge color="gray" size="xs" variant="outline">Brunch ✗</Badge>
                            )}
                            <ActionIcon
                              size="xs"
                              variant="subtle"
                              color="gray"
                              onClick={() => handleEditGuest(guest)}
                            >
                              <IconEdit size={12} />
                            </ActionIcon>
                          </Group>
                        ))}
                      </div>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        {attending > 0 && <Badge color="green" size="sm">{attending} ✓</Badge>}
                        {notAttending > 0 && <Badge color="red" size="sm">{notAttending} ✗</Badge>}
                        {noResponse > 0 && <Badge color="gray" size="sm">{noResponse} ?</Badge>}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <div style={{ fontSize: "0.875rem" }}>
                        {family.dietary_restrictions || <span style={{ color: "var(--mantine-color-dimmed)" }}>None</span>}
                      </div>
                    </Table.Td>
                    <Table.Td>
                      <Button 
                        size="xs" 
                        variant="light"
                        onClick={() => handleAddGuest(family.id)}
                      >
                        + Guest
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </div>
      </Paper>

      <AddFamilyModal
        opened={addFamilyModalOpen}
        onClose={() => setAddFamilyModalOpen(false)}
        onSuccess={loadData}
      />

      <AddGuestModal
        opened={addGuestModalOpen}
        onClose={() => {
          setAddGuestModalOpen(false);
          setSelectedFamilyId(null);
        }}
        familyId={selectedFamilyId}
        families={families}
        onSuccess={loadData}
      />

      <EditFamilyModal
        opened={editFamilyModalOpen}
        onClose={() => {
          setEditFamilyModalOpen(false);
          setSelectedFamily(null);
        }}
        family={selectedFamily}
        onSuccess={loadData}
      />

      <EditGuestModal
        opened={editGuestModalOpen}
        onClose={() => {
          setEditGuestModalOpen(false);
          setSelectedGuest(null);
        }}
        guest={selectedGuest}
        onSuccess={loadData}
      />
    </div>
  );
}
